import { Controller, type UseFormReturn } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { Button } from '../Button'
import { Input } from '../FormElements/Input'
import { CountrySelector } from '../CountrySelector'
import { isEmailAvailable } from '../../utils/isEmailAvailable'
import { useAuth } from '../../contexts/AuthContext'

type InternationalPersonFormPresentationalProps = UseFormReturn<any> & {
  isSubmitting: boolean
  submitButtonText: string
  onCancel: () => void
  onSubmit: (data: InternationalPersonFormData) => void
}

export type InternationalPersonFormData = {
  name: string
  email: string
  phone: string
  linkedin?: string
  city: string
  state: string
  country: string
}

export const InternationalPersonFormPresentational = ({
  isSubmitting,
  submitButtonText,
  onCancel,
  onSubmit,
  ...props
}: InternationalPersonFormPresentationalProps) => {
  const { formatMessage } = useIntl()
  const { user } = useAuth()

  return (
    <form onSubmit={props.handleSubmit(onSubmit)}>
      <div className="w-full flex flex-col gap-[20px]">
        <Controller
          name="name"
          control={props.control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <Input
              label={formatMessage({ id: 'FULL_NAME' })}
              placeholder={formatMessage({ id: 'FULL_NAME' })}
              isValid={false}
              value={value}
              onChange={onChange}
              errorMessage={
                error?.message && formatMessage({ id: `${error?.message}` })
              }
            />
          )}
        />

        <div className="flex flex-col gap-5 md:flex-row md:gap-2">
          <div className="md:grow">
            <Controller
              name="email"
              control={props.control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <Input
                  label="E-mail"
                  placeholder="exemple@email.com"
                  isValid={false}
                  value={value}
                  onChange={async (event) => {
                    onChange(event)

                    const emailFieldValue = event.target.value
                    const currentUserEmail = user?.email
                    const isNotEmailAvailable = !(await isEmailAvailable(
                      emailFieldValue,
                      currentUserEmail
                    ))

                    if (isNotEmailAvailable) {
                      props.setError('email', {
                        message: 'EMAIL_ALREADY_IN_USE',
                      })
                      return
                    }

                    props.clearErrors('email')
                  }}
                  errorMessage={
                    error?.message && formatMessage({ id: error?.message })
                  }
                />
              )}
            />
          </div>

          <div className="w-36 md:w-44">
            <Controller
              name="phone"
              control={props.control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <Input
                  label={formatMessage({ id: 'TELEPHONE' })}
                  placeholder={formatMessage({ id: 'TELEPHONE' })}
                  isValid={false}
                  value={value}
                  onChange={onChange}
                  errorMessage={
                    error?.message && formatMessage({ id: `${error?.message}` })
                  }
                />
              )}
            />
          </div>
        </div>

        <Controller
          name="linkedin"
          control={props.control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <Input
              label="LinkedIn"
              placeholder="https://www.linkedin.com/in/example-user-name"
              isValid={false}
              value={value}
              onChange={onChange}
              errorMessage={
                error?.message && formatMessage({ id: `${error?.message}` })
              }
            />
          )}
        />

        <div className="flex flex-col gap-5 md:flex-row md:gap-2">
          <Controller
            name="city"
            control={props.control}
            render={({
              field: { value = '', onChange },
              fieldState: { error },
            }) => (
              <Input
                label={formatMessage({ id: 'CITY' })}
                placeholder={formatMessage({ id: 'CITY' })}
                isValid={false}
                value={value}
                onChange={onChange}
                errorMessage={
                  error?.message && formatMessage({ id: `${error?.message}` })
                }
              />
            )}
          />

          <Controller
            name="state"
            control={props.control}
            render={({
              field: { value = '', onChange },
              fieldState: { error },
            }) => (
              <Input
                label={formatMessage({ id: 'STATE' })}
                placeholder={formatMessage({ id: 'STATE' })}
                isValid={false}
                value={value}
                onChange={onChange}
                errorMessage={
                  error?.message && formatMessage({ id: `${error?.message}` })
                }
              />
            )}
          />
        </div>

        <Controller
          name="country"
          control={props.control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <CountrySelector
              label={formatMessage({
                id: 'WHY_IS_IMPORTANT_REGISTER_YOUR_LOCATION',
              })}
              value={value}
              onValueChange={onChange}
              placeholderMessageId={'COUNTRY'}
              errorMessage={
                error?.message && formatMessage({ id: `${error?.message}` })
              }
            />
          )}
        />
      </div>

      <div className="flex flex-row gap-2 mt-14">
        <Button type="button" variant="outline" onClick={onCancel}>
          {formatMessage({ id: 'CANCEL' })}
        </Button>

        <Button
          disabled={
            !props.formState.isValid ||
            Object.entries(props.formState.errors).length > 0
          }
          isLoading={isSubmitting}
          type="submit"
        >
          {submitButtonText}
        </Button>
      </div>
    </form>
  )
}
